'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import PrimaryButton from '@/components/shared/PrimaryButton';
import FooterProgressiveBlur from '@/components/comix/FooterProgressiveBlur';
import HeroSection from '@/components/comix/home/HeroSection';
import { trackEvent } from '@/lib/amplitude';
import { COMIX_THEMES_ENABLED } from '@/lib/comix/themesConfig';

export default function ComixHomeClient() {
  const router = useRouter();
  const [hasCharacters, setHasCharacters] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const existingSessionId = Cookies.get('comixSessionId');
    if (!existingSessionId) return;

    fetch(`/api/comix/characters?sessionId=${existingSessionId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setHasCharacters((data?.characters?.length ?? 0) > 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const target = heroCtaRef.current;
    if (!target) return;

    // Watch the hero CTA against the actual scroll container (<main>), not the
    // viewport. Inside a nested scroller, a viewport-rooted observer on iOS
    // Safari fires late / flickers around the edge, which toggled the fixed bar
    // — and its <main> padding-bottom mutation below — rapidly under the user's
    // finger. A small root threshold debounces the crossing.
    const root = target.closest('main');
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { root: root ?? null, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  // The sticky bar is position:fixed, so it overlaps the end of the scroll area. Reserve
  // room for it by padding the *scroll container's* bottom — not with an in-flow spacer
  // after the hero, which shoved every section below it (StoryBanner onwards) down by 80px
  // the moment the bar appeared, visible as a jump in the gap above "Multiple characters."
  // Measure the bar's real height (button + py-4 + safe-area inset) rather than hard-coding
  // 80px, which wasn't enough and left the footer's legal links tucked behind the bar.
  useEffect(() => {
    const scroller =
      heroCtaRef.current?.closest('main') ?? document.querySelector('main');
    if (!(scroller instanceof HTMLElement)) return;

    const applyPadding = () => {
      // offsetHeight is the border box — includes py-4 AND the safe-area inset padding.
      const barHeight = stickyBarRef.current?.offsetHeight ?? 80;
      scroller.style.paddingBottom = showStickyCta ? `${barHeight + 16}px` : '';
    };
    applyPadding();

    if (!showStickyCta) {
      return () => {
        scroller.style.paddingBottom = '';
      };
    }

    // The bar's height can change after mount: fonts finish loading, or the safe-area
    // inset changes on rotation / when the mobile browser toolbar shows and hides. A
    // ResizeObserver is no use here — a padding-only change (which is all the safe-area
    // inset is) never fires it — so re-measure on the viewport events that do coincide
    // with those changes. rAF lets the new layout settle before we read offsetHeight.
    let raf = 0;
    const remeasure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyPadding);
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    window.visualViewport?.addEventListener('resize', remeasure);
    document.fonts?.ready.then(applyPadding).catch(() => {});

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
      window.visualViewport?.removeEventListener('resize', remeasure);
      scroller.style.paddingBottom = '';
    };
  }, [showStickyCta]);

  const ctaLabel = hasCharacters ? 'Continue Comic' : 'Get Started';

  const handleGetStarted = async (section: string) => {
    trackEvent("Comix Home - Get Started", { section });
    const existingSessionId = Cookies.get('comixSessionId');

    if (!existingSessionId) {
      // Themes step is off by default (see themesConfig.ts) — a brand-new
      // visitor with no session yet has no characters, so skip straight to create.
      router.push(COMIX_THEMES_ENABLED ? '/comix/themes' : '/comix/create');
      return;
    }

    try {
      const res = await fetch(`/api/comix/characters?sessionId=${existingSessionId}`);
      const data = res.ok ? await res.json() : null;
      const hasCharacters = (data?.characters?.length ?? 0) > 0;
      router.push(hasCharacters ? '/comix/select' : '/comix/create');
    } catch {
      router.push('/comix/select');
    }
  };

  return (
    <>
      <HeroSection
        ctaLabel={ctaLabel}
        onGetStarted={() => handleGetStarted('hero')}
        ctaRef={heroCtaRef}
      />

      {/* Sticky CTA - shown once the hero CTA has scrolled out of view */}
      {showStickyCta && (
        <div ref={stickyBarRef} className="fixed bottom-0 left-0 right-0 py-4 z-50" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <FooterProgressiveBlur />
          <div className="container relative z-[1] mx-auto flex justify-center px-4">
            <PrimaryButton
              id="btn-get-started-sticky"
              onClick={() => handleGetStarted("sticky")}
              className="w-full max-w-2xl"
            >
              {ctaLabel}
            </PrimaryButton>
          </div>
        </div>
      )}
    </>
  );
}
